package fr.manyverse.app.infrastructure.audio

import kotlinx.coroutines.flow.StateFlow

/**
 * Playback state for a single audio player instance.
 */
data class PlaybackState(
    val isPlaying: Boolean = false,
    val soundId: String? = null,
    val volume: Float = 1.0f,
    val isLooping: Boolean = true,
    val currentPositionMs: Long = 0L,
    val durationMs: Long = 0L
) {
    /**
     * Progress as a fraction from 0.0 to 1.0.
     */
    val progress: Float
        get() = if (durationMs > 0) (currentPositionMs.toFloat() / durationMs).coerceIn(
            0f,
            1f
        ) else 0f
}

/**
 * Platform-specific audio player interface.
 * Each platform provides its own implementation.
 */
interface AudioPlayer {
    /**
     * Current playback state as observable flow.
     */
    val playbackState: StateFlow<PlaybackState>

    /**
     * Prepares and plays audio from byte data.
     * @param audioData The audio file bytes
     * @param soundId Identifier for tracking which sound is playing
     * @param loop Whether to loop the audio
     */
    suspend fun play(audioData: ByteArray, soundId: String, loop: Boolean = true)

    /**
     * Pauses playback.
     */
    fun pause()

    /**
     * Resumes playback.
     */
    fun resume()

    /**
     * Stops playback and releases resources.
     */
    fun stop()

    /**
     * Sets the volume level.
     * @param volume Volume from 0.0 (mute) to 1.0 (full)
     */
    fun setVolume(volume: Float)

    /**
     * Releases all resources. Call when done with player.
     */
    fun release()
}

/**
 * Factory function to create platform-specific AudioPlayer.
 */
expect fun createAudioPlayer(): AudioPlayer
